/**
 * Sprint 9 — super-admin-only GDPR anonymize modal.
 *
 * Surfaces the existing /api/admin/respondents/:id/anonymize endpoint
 * with a confirm step. The protective step is requiring the operator to
 * type ANONYMIZE before the action button arms — a clean equivalent of
 * "type DELETE to confirm" without the irreversibility-language that
 * misrepresents what actually happens (rows stay; identifiers are
 * wiped; item_responses preserved for aggregate-math integrity).
 *
 * Sprint 9 refactor — extracted from Respondents.jsx (744L → 615L)
 * to keep that file under 500 lines.
 */
import React from "react";
import { api } from "../lib/api";


export default function AnonymizeModal({ respondent, onClose, onSuccess }) {
  const [reason, setReason] = React.useState("");
  const [confirmation, setConfirmation] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const armed = confirmation.trim() === "ANONYMIZE";

  async function submit(e) {
    e.preventDefault();
    if (!armed) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/api/admin/respondents/${respondent.id}/anonymize`, {
        reason: reason.trim(),
      });
      await onSuccess();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Couldn't anonymize");
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(26,26,26,0.4)" }}
      data-testid="anonymize-modal"
      onClick={onClose}
    >
      <div
        className="bg-paper p-8 w-full max-w-lg"
        style={{ border: "1px solid #DDDDDD" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>GDPR · Anonymize respondent</div>
        <h3 className="text-xl mb-3" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          {respondent.email || "(no email on file)"}
        </h3>
        <div className="text-sm text-gray-700 max-w-prose mb-4">
          <p className="mb-3">This will:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Blank</strong> the respondent&rsquo;s demographics and email</li>
            <li><strong>Replace</strong> every free-text reflection with the sentinel <code>[anonymized]</code></li>
            <li><strong>Preserve</strong> the respondent&rsquo;s item scores so dimension aggregates stay intact for this deployment</li>
            <li><strong>Write</strong> an entry to the audit log with your identity, timestamp, and the reason below</li>
          </ul>
          <p className="mt-3">
            The action is GDPR Article 17&ndash;compliant erasure of identifying data. The respondent
            row itself stays so aggregate math is unaffected for the cohort.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label" htmlFor="anonymize-reason">Reason for the request (recorded in audit log)</label>
            <textarea
              id="anonymize-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Subject access request received 2026-06-06; verified identity via consultant"
              rows={3}
              maxLength={500}
              className="field w-full text-sm"
              style={{ fontFamily: "Inter", lineHeight: 1.5 }}
              data-testid="anonymize-reason"
            />
            <div className="text-xs text-gray-500 mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.08em" }}>
              {reason.length} / 500
            </div>
          </div>
          <div>
            <label className="label" htmlFor="anonymize-confirm">
              Type <strong>ANONYMIZE</strong> to confirm
            </label>
            <input
              id="anonymize-confirm"
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className="field w-full text-sm"
              style={{ fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase" }}
              data-testid="anonymize-confirm"
              autoComplete="off"
            />
          </div>
          {error && (
            <div className="text-sm" style={{ color: "#AD393E" }} data-testid="anonymize-error">{error}</div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-quiet"
              data-testid="anonymize-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!armed || submitting}
              data-testid="anonymize-submit"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                fontSize: "11px", color: "#FBFAF7",
                background: armed && !submitting ? "#AD393E" : "#6F6F6F",
                border: 0, padding: "10px 18px",
                cursor: armed && !submitting ? "pointer" : "not-allowed",
              }}
            >
              {submitting ? "Anonymizing…" : "Anonymize"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
